import { ParentSize } from '@visx/responsive'
import moment from 'moment'
import { ReactElement, useMemo, useState } from 'react'
import { SectionCard } from 'app/components/SectionCard'
import Spinner from 'app/components/Spinner'
import DateGroupSelector, { GroupBy } from 'app/components/viz/DateGroupSelector'
import BarStackGraph from 'app/components/viz/graphs/BarStackGraph'
import { ActivityJson } from 'app/types/entities'
import { InProgressLegend } from './InProgressLegend'
import {
  SERIES_KEY,
  SCHEDULED_KEY,
  NOT_SCHEDULED_KEY,
  SCHEDULED_COLOR,
  NOT_SCHEDULED_COLOR,
  bucketByScheduled,
  groupControlsForRange,
  inProgressRange,
  resolveGroup,
} from './newPatientCallsBuckets'

const ALL_KEYS = [SCHEDULED_KEY, NOT_SCHEDULED_KEY]
const COLOR_MAP: Record<string, string> = {
  [SCHEDULED_KEY]: SCHEDULED_COLOR,
  [NOT_SCHEDULED_KEY]: NOT_SCHEDULED_COLOR,
}

/**
 * New Patient calls over time, split Scheduled vs Not Scheduled. Renders through
 * the shared `BarStackGraph` — the same stacked-bar component the Marketing
 * dashboards (e.g. Leads) use — so the axes, tilted date labels, and bottom
 * legend match those dashboards. `integerYAxis` keeps the low call counts on
 * whole-number ticks. Bars run through today, never past it; when today falls in
 * the last bucket, a dashed divider marks it as still filling up (as on the Ops tab).
 */
export function NewPatientCallsGraph({
  activities,
  fromDate,
  toDate,
  total,
  isLoading,
}: {
  activities: ActivityJson[]
  fromDate: string
  toDate: string
  total?: number
  isLoading?: boolean
}): ReactElement {
  const controls = groupControlsForRange(fromDate, toDate)
  const [group, setGroup] = useState<GroupBy>(controls.defaultGroup)
  const effectiveGroup = resolveGroup(group, controls)

  // The UTC day, unlike Ops: this tab fetches, buckets, and lists calls by UTC date, so
  // a local "today" would cut off a US evening's calls that already sit in tomorrow's bucket.
  const { lastDay, lastBarInProgress } = inProgressRange(fromDate, toDate, moment.utc().format(`YYYY-MM-DD`))

  const buckets = useMemo(
    () => bucketByScheduled(activities, fromDate, lastDay, effectiveGroup),
    [activities, fromDate, lastDay, effectiveGroup],
  )

  const data = useMemo(
    () =>
      buckets.map((b, index) => ({
        day: b.label,
        tooltipDisplay: lastBarInProgress && index === buckets.length - 1 ? `${b.tooltip} (in progress)` : b.tooltip,
        values: { [SCHEDULED_KEY]: b.scheduled, [NOT_SCHEDULED_KEY]: b.notScheduled },
      })),
    [buckets, lastBarInProgress],
  )

  const totals = useMemo(
    () => ({
      [SCHEDULED_KEY]: buckets.reduce((sum, b) => sum + b.scheduled, 0),
      [NOT_SCHEDULED_KEY]: buckets.reduce((sum, b) => sum + b.notScheduled, 0),
    }),
    [buckets],
  )

  return (
    <SectionCard
      attached
      title={SERIES_KEY}
      className="mb-6"
      contentClassName="bg-surface"
      headerButtons={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <DateGroupSelector
            defaultGroup={effectiveGroup}
            disableDays={controls.disableDays}
            disableWeeks={controls.disableWeeks}
            disableMonths={controls.disableMonths}
            onGroupChange={setGroup}
            hideLabel
          />
        </div>
      }
    >
      <div>
        <div className="h-[360px]">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size={16} />
            </div>
          ) : (
            <ParentSize>
              {({ width, height }) =>
                width && height ? (
                  <BarStackGraph
                    width={width}
                    height={height}
                    data={data}
                    allKeys={ALL_KEYS}
                    totals={totals}
                    colorMap={COLOR_MAP}
                    integerYAxis
                    lastBarInProgress={lastBarInProgress}
                    bottomRightSlot={lastBarInProgress ? <InProgressLegend group={effectiveGroup} /> : undefined}
                    legendSuffix={
                      <span className="mx-3 flex items-center whitespace-nowrap text-body-sm text-default">
                        Total
                        <span className="ml-1 text-ui-sm text-default">
                          {(total ?? activities.length).toLocaleString(`en-US`)}
                        </span>
                      </span>
                    }
                  />
                ) : null
              }
            </ParentSize>
          )}
        </div>
      </div>
    </SectionCard>
  )
}
